import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, Fragment } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import Switch from "@mui/material/Switch";
import { useFormik } from "formik";
import PropTypes from "prop-types";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppButton from "components/app-button";
import AppCheckbox from "components/app-checkbox";
import AppSelectInput from "components/app-select-input";
import AppTimeInput from "components/app-time-input";
import classNames from "common/class-names";
import { FREQUENCY, WEEKDAY_CODES, WEEKDAY_LABELS, MONTH_NAMES, maxDayForMonth, parseCronToBuilder, buildCronFromValues } from "common/cron-schedule";

const FREQUENCY_OPTIONS = [
	{ label: "Daily", value: FREQUENCY.DAILY },
	{ label: "Weekly", value: FREQUENCY.WEEKLY },
	{ label: "Monthly", value: FREQUENCY.MONTHLY },
	{ label: "Yearly", value: FREQUENCY.YEARLY },
];

const MONTH_OPTIONS = MONTH_NAMES.map((label, i) => ({ label, value: String(i + 1) }));

const MAX_DAILY_TIMES = 6;

const formatHour = (hour) => `${String(hour).padStart(2, "0")}:00`;

const defaultBuilderValues = () => ({
	daily: { times: ["09:00"] },
	weekly: { days: ["MON"], time: "09:00" },
	monthly: { day: 1, lastDay: false, time: "09:00" },
	yearly: { month: 1, day: 1, time: "09:00" },
});

const buildInitialValues = (job) => {
	const base = { frequency: null, unsupportedExpression: null, enabled: job?.enabled ?? true, ...defaultBuilderValues() };

	if (!job?.cronExpression) return { ...base, frequency: FREQUENCY.DAILY };

	const parsed = parseCronToBuilder(job.cronExpression);

	if (parsed.unsupported) {
		return { ...base, frequency: null, unsupportedExpression: job.cronExpression };
	}

	if (parsed.daily) parsed.daily = { times: [...new Set(parsed.daily.times.map((time) => `${time.slice(0, 2)}:00`))] };

	const key = parsed.frequency.toLowerCase();
	return { ...base, frequency: parsed.frequency, [key]: { ...base[key], ...parsed[key] } };
};

const FrequencySelector = memo(({ value, disabled, onChange }) => {
	return (
		<div className="scheduler-modal__frequency">
			{FREQUENCY_OPTIONS.map((option) => (
				<button
					key={option.value}
					type="button"
					disabled={disabled}
					className={classNames({ "scheduler-modal__frequency-option": true, "scheduler-modal__frequency-option--active": value === option.value })}
					onClick={() => onChange(option.value)}
				>
					{option.label}
				</button>
			))}
		</div>
	);
});

FrequencySelector.propTypes = { value: PropTypes.string, disabled: PropTypes.bool, onChange: PropTypes.func.isRequired };

const DailyFields = memo(({ times, disabled, error, onChange }) => {
	//prettier-ignore
	const onHandleTimeChange = useCallback((index, value) => {
		const next = times.slice();
		next[index] = value;
		onChange(next);
	}, [times, onChange]);

	//prettier-ignore
	const onHandleRemove = useCallback((index) => {
		onChange(times.filter((_, i) => i !== index));
	}, [times, onChange]);

	const onHandleAdd = useCallback(() => {
		const hour = [9, ...Array(24).keys()].find((h) => !times.includes(formatHour(h)));
		onChange([...times, formatHour(hour)]);
	}, [times, onChange]);

	return (
		<div className="scheduler-modal__field">
			<p className="scheduler-modal__caption">Times</p>

			<div className="scheduler-modal__time-grid">
				{times.map((time, index) => (
					<div className="scheduler-modal__time-slot" key={index}>
						<AppTimeInput lockMinute clearable={false} name={`dailyTime${index}`} className="scheduler-modal__time-picker" placeholder="--:--" value={time} disabled={disabled} onChange={(name, value) => onHandleTimeChange(index, value)} />
						{times.length > 1 && (
							<button type="button" className="scheduler-modal__time-remove" disabled={disabled} onClick={() => onHandleRemove(index)}>
								×
							</button>
						)}
					</div>
				))}

				{times.length < MAX_DAILY_TIMES && (
					<button type="button" className="scheduler-modal__time-add" disabled={disabled} onClick={onHandleAdd}>
						+ Add time
					</button>
				)}
			</div>

			<p className="scheduler-modal__counter">{`${times.length} of ${MAX_DAILY_TIMES} run times`}</p>
			{error && <p className="scheduler-modal__error">{error}</p>}
		</div>
	);
});

DailyFields.propTypes = { times: PropTypes.array.isRequired, disabled: PropTypes.bool, error: PropTypes.string, onChange: PropTypes.func.isRequired };

const WeeklyFields = memo(({ days, time, disabled, error, onChange }) => {
	//prettier-ignore
	const onHandleToggleDay = useCallback((code) => {
		const active = days.includes(code);
		const next = active ? days.filter((d) => d !== code) : [...days, code];
		onChange({ days: next, time });
	}, [days, time, onChange]);

	const onHandleTimeChange = useCallback((name, value) => onChange({ days, time: value }), [days, onChange]);

	return (
		<Fragment>
			<div className="scheduler-modal__field">
				<p className="scheduler-modal__caption">Days</p>

				<div className="scheduler-modal__day-pills">
					{WEEKDAY_CODES.map((code) => (
						<button
							key={code}
							type="button"
							disabled={disabled}
							className={classNames({ "scheduler-modal__day-pill": true, "scheduler-modal__day-pill--active": days.includes(code) })}
							onClick={() => onHandleToggleDay(code)}
						>
							{WEEKDAY_LABELS[code]}
						</button>
					))}
				</div>

				{error && <p className="scheduler-modal__error">{error}</p>}
			</div>

			<div className="scheduler-modal__field">
				<p className="scheduler-modal__caption">Time</p>
				<AppTimeInput clearable={false} name="weeklyTime" className="scheduler-modal__time-picker" placeholder="--:--" value={time} disabled={disabled} onChange={onHandleTimeChange} />
			</div>
		</Fragment>
	);
});

WeeklyFields.propTypes = { days: PropTypes.array.isRequired, time: PropTypes.string.isRequired, disabled: PropTypes.bool, error: PropTypes.string, onChange: PropTypes.func.isRequired };

const MonthlyFields = memo(({ day, lastDay, time, disabled, onChange }) => {
	//prettier-ignore
	const onHandleDayChange = useCallback((event) => {
		const value = event.target.value.replace(/[^\d]/g, "");
		onChange({ day: value ? Math.min(31, parseInt(value, 10)) : "", lastDay, time });
	}, [lastDay, time, onChange]);

	const onHandleTimeChange = useCallback((name, value) => onChange({ day, lastDay, time: value }), [day, lastDay, onChange]);

	const onHandleToggleLastDay = useCallback(() => onChange({ day, lastDay: !lastDay, time }), [day, lastDay, time, onChange]);

	const showWarning = useMemo(() => !lastDay && day >= 29, [lastDay, day]);

	return (
		<Fragment>
			<div className="scheduler-modal__row">
				<div className="scheduler-modal__field">
					<p className="scheduler-modal__caption">Day of month</p>
					<input type="number" min={1} max={31} className="scheduler-modal__number-input" value={day} disabled={disabled || lastDay} onChange={onHandleDayChange} />
				</div>

				<div className="scheduler-modal__field">
					<p className="scheduler-modal__caption">Time</p>
					<AppTimeInput clearable={false} name="monthlyTime" className="scheduler-modal__time-picker" placeholder="--:--" value={time} disabled={disabled} onChange={onHandleTimeChange} />
				</div>
			</div>

			{showWarning && <p className="scheduler-modal__warning">Shorter months may not have the respective day, so those months are skipped.</p>}

			<AppCheckbox name="lastDay" label="Run on the last day of the month" value={lastDay} disabled={disabled} onClick={onHandleToggleLastDay} />
		</Fragment>
	);
});

MonthlyFields.propTypes = { day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), lastDay: PropTypes.bool.isRequired, time: PropTypes.string.isRequired, disabled: PropTypes.bool, onChange: PropTypes.func.isRequired };

const YearlyFields = memo(({ month, day, time, disabled, error, onChange }) => {
	const onHandleMonthChange = useCallback((event) => {
		const nextMonth = parseInt(event.target.value, 10);
		const maxDay = maxDayForMonth(nextMonth);
		onChange({ month: nextMonth, day: Math.min(day, maxDay), time });
	}, [day, time, onChange]);

	//prettier-ignore
	const onHandleDayChange = useCallback((event) => {
		const value = event.target.value.replace(/[^\d]/g, "");
		onChange({ month, day: value ? Math.min(31, parseInt(value, 10)) : "", time });
	}, [month, time, onChange]);

	const onHandleTimeChange = useCallback((name, value) => onChange({ month, day, time: value }), [month, day, onChange]);

	return (
		<div className="scheduler-modal__row scheduler-modal__row--three">
			<div className="scheduler-modal__field">
				<p className="scheduler-modal__caption">Month</p>
				<AppSelectInput searchable={false} name="yearlyMonth" placeholder="Select month" options={MONTH_OPTIONS} value={String(month)} disabled={disabled} onChange={onHandleMonthChange} />
			</div>

			<div className="scheduler-modal__field">
				<p className="scheduler-modal__caption">Day</p>
				<input type="number" min={1} max={31} className="scheduler-modal__number-input" value={day} disabled={disabled} onChange={onHandleDayChange} />
				{error && <p className="scheduler-modal__error">{error}</p>}
			</div>

			<div className="scheduler-modal__field">
				<p className="scheduler-modal__caption">Time</p>
				<AppTimeInput clearable={false} name="yearlyTime" className="scheduler-modal__time-picker" placeholder="--:--" value={time} disabled={disabled} onChange={onHandleTimeChange} />
			</div>
		</div>
	);
});

YearlyFields.propTypes = { month: PropTypes.number.isRequired, day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), time: PropTypes.string.isRequired, disabled: PropTypes.bool, error: PropTypes.string, onChange: PropTypes.func.isRequired };

export const AppSchedulerModal = (props, ref) => {
	const dispatch = useDispatch();
	const [job, setJob] = useState();
	const [visible, setVisible] = useState(false);
	const initialValues = useMemo(() => buildInitialValues(job), [job]);

	//prettier-ignore
	const onHandleValidate = useCallback((values) => {
		const errors = {};

		if (!values.frequency) return errors;

		if (values.frequency === FREQUENCY.DAILY) {
			const times = values.daily.times.filter(Boolean);
			if (!times.length) errors.daily = ERRORS.REQUIRED;
			else if (new Set(times).size !== times.length) errors.daily = "Duplicate times are not allowed";
		}

		if (values.frequency === FREQUENCY.WEEKLY && !values.weekly.days.length) {
			errors.weekly = ERRORS.MINIMUM_DAY;
		}

		if (values.frequency === FREQUENCY.YEARLY) {
			const maxDay = maxDayForMonth(values.yearly.month);
			if (!values.yearly.day || values.yearly.day > maxDay) errors.yearly = `${MONTH_NAMES[values.yearly.month - 1]} has only ${maxDay} days`;
		}

		return errors;
	}, []);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		validate: onHandleValidate,
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const onHandleShow = useCallback((data) => {
		setJob(data);
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		formik.resetForm();
		setVisible(false);
	}, [formik]);

	//prettier-ignore
	const onHandleToggleEnabled = useCallback((event) => {
		formik.setFieldValue("enabled", event.target.checked);
	}, [formik]);

	const onHandleFrequencyChange = useCallback((value) => {
		formik.setFieldValue("frequency", value);
	}, [formik]);

	const onHandleReplaceUnsupported = useCallback(() => {
		formik.setFieldValue("frequency", FREQUENCY.DAILY);
	}, [formik]);

	const onHandleDailyChange = useCallback((times) => formik.setFieldValue("daily", { times }), [formik]);
	const onHandleWeeklyChange = useCallback((weekly) => formik.setFieldValue("weekly", weekly), [formik]);
	const onHandleMonthlyChange = useCallback((monthly) => formik.setFieldValue("monthly", monthly), [formik]);
	const onHandleYearlyChange = useCallback((yearly) => formik.setFieldValue("yearly", yearly), [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;

		const cronExpression = values.frequency ? buildCronFromValues(values.frequency, values) : values.unsupportedExpression;

		try {
			const payload = {
				cronExpression,
				enabled: values.enabled,
			};

			response = await api.put.scheduler.update(job.jobKey, payload);
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: "Saved and active." }));
			props.onHandleUpdated(response);
		}
	}, [dispatch, onHandleDismiss, job, formik, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-scheduler-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="scheduler-modal" aria-describedby="scheduler-modal">
			<div className="scheduler-modal">
				<div className="scheduler-modal__main">
					<div className="scheduler-modal__header">
						<p className="scheduler-modal__title">Edit Scheduler</p>
					</div>

					<form className="scheduler-modal__body" onSubmit={formik.handleSubmit}>
						<div className="scheduler-modal__info">
							<p className="scheduler-modal__info-label">Scheduler</p>
							<p className="scheduler-modal__info-value">{job?.jobName}</p>
						</div>

						{formik.values.unsupportedExpression && !formik.values.frequency && (
							<div className="scheduler-modal__field">
								<p className="scheduler-modal__caption">Current schedule</p>
								<p className="scheduler-modal__readonly-expression">{formik.values.unsupportedExpression}</p>
								<p className="scheduler-modal__hint">This schedule was set outside the builder and can't be edited here.</p>
								<AppButton type="button" outline label="Replace with a new frequency" disabled={formik.isSubmitting} onClick={onHandleReplaceUnsupported} />
							</div>
						)}

						{formik.values.frequency && (
							<Fragment>
								<div className="scheduler-modal__field">
									<p className="scheduler-modal__caption">Runs</p>
									<FrequencySelector value={formik.values.frequency} disabled={formik.isSubmitting} onChange={onHandleFrequencyChange} />
								</div>

								{formik.values.frequency === FREQUENCY.DAILY && (
									<DailyFields times={formik.values.daily.times} disabled={formik.isSubmitting} error={formik.errors.daily} onChange={onHandleDailyChange} />
								)}

								{formik.values.frequency === FREQUENCY.WEEKLY && (
									<WeeklyFields days={formik.values.weekly.days} time={formik.values.weekly.time} disabled={formik.isSubmitting} error={formik.errors.weekly} onChange={onHandleWeeklyChange} />
								)}

								{formik.values.frequency === FREQUENCY.MONTHLY && (
									<MonthlyFields day={formik.values.monthly.day} lastDay={formik.values.monthly.lastDay} time={formik.values.monthly.time} disabled={formik.isSubmitting} onChange={onHandleMonthlyChange} />
								)}

								{formik.values.frequency === FREQUENCY.YEARLY && (
									<YearlyFields month={formik.values.yearly.month} day={formik.values.yearly.day} time={formik.values.yearly.time} disabled={formik.isSubmitting} error={formik.errors.yearly} onChange={onHandleYearlyChange} />
								)}
							</Fragment>
						)}

						<div className="scheduler-modal__toggle">
							<p className="scheduler-modal__toggle-text">
								<span className="scheduler-modal__toggle-label">Status</span>
								<span className="scheduler-modal__toggle-description">{formik.values.enabled ? "Enabled" : "Disabled"}</span>
							</p>

							<Switch name="enabled" checked={formik.values.enabled} disabled={formik.isSubmitting} onChange={onHandleToggleEnabled} />
						</div>

						<div className="scheduler-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Save" disabled={formik.isSubmitting || !formik.isValid} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppSchedulerModal));

AppSchedulerModal.propTypes = {
	ref: PropTypes.object,
	onHandleUpdated: PropTypes.func,
};
